import axios from 'axios';
import axiosInstance from '../../../api/protected';
import {
  ReportDocument,
  ReportError,
  ReportListParams,
  ReportRunOverrides,
  ReportTemplate,
  SaveReportTemplate,
} from '../types/report-builder.types';

const readBlobText = (blob: Blob): Promise<string> => {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

export const getReportError = async (error: unknown, fallback: string): Promise<ReportError> => {
  let payload: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  if (payload instanceof Blob) {
    try {
      payload = JSON.parse(await readBlobText(payload));
    } catch {
      payload = undefined;
    }
  }
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = undefined;
    }
  }
  if (payload && typeof payload === 'object') {
    const {code, message, title} = payload as Record<string, unknown>;
    return {
      ...(typeof code === 'string' ? {code} : {}),
      message: typeof message === 'string' && message.trim() ? message : typeof title === 'string' ? title : fallback,
    };
  }
  if (axios.isAxiosError(error) && [401, 403].includes(error.response?.status || 0)) {
    return {message: 'Недостаточно прав для выполнения действия.'};
  }
  return {message: !axios.isAxiosError(error) && error instanceof Error && error.message ? error.message : fallback};
};

export const reportsApi = {
  async list(params: ReportListParams = {}): Promise<ReportTemplate[]> {
    const {data} = await axiosInstance.get<ReportTemplate[]>('/reports', {
      params: {
        ...params,
        myOnly: params.myOnly ?? false,
        name: params.name?.trim() || undefined,
        description: params.description || undefined,
      },
      headers: {Accept: 'application/json'},
    });
    return data;
  },

  async save(payload: SaveReportTemplate): Promise<ReportTemplate> {
    const {data} = await axiosInstance.post<ReportTemplate>('/reports', payload, {
      headers: {Accept: 'application/json'},
    });
    return data;
  },

  async remove(id: number): Promise<void> {
    await axiosInstance.delete('/reports', {params: {id}});
  },

  async getFields(): Promise<unknown> {
    const {data} = await axiosInstance.get<unknown>('/export/report/universalModel', {
      headers: {Accept: 'application/json'},
    });
    return data;
  },

  async generate(id: number, overrides?: ReportRunOverrides): Promise<ReportDocument> {
    const {data} = await axiosInstance.post<ReportDocument>('/export/report/universal', overrides, {
      params: {id},
      headers: {Accept: 'application/json'},
    });
    if (!data.id) throw new Error('Сервер не вернул файл сформированного отчета.');
    return {id: data.id, fileName: data.fileName || 'report.xlsx'};
  },

  async download(report: ReportDocument): Promise<void> {
    const {data} = await axiosInstance.get<Blob>(`/documents/${encodeURIComponent(report.id)}/content`, {
      responseType: 'blob',
      headers: {Accept: '*/*'},
    });
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    try {
      link.href = url;
      link.download = report.fileName;
      document.body.appendChild(link);
      link.click();
    } finally {
      link.remove();
      URL.revokeObjectURL(url);
    }
  },
};
